import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame } from '../types';
import { at, pushRect, pushSegment } from '../glyphs/frame';
import type { ParamValues } from '../paramSchema';
import { t } from '../../translation/markers';

const PARAMS = {
  kind: {
    kind: 'select',
    label: t('type'),
    default: 'double',
    options: [
      { value: 'single', label: t('single') },
      { value: 'double', label: t('double') },
    ],
  },
} as const;

/** Pillow inset from the mattress edges (m). */
const PILLOW_MARGIN = 0.08;
/** Blanket line gap below the pillow zone (m). */
const BLANKET_GAP = 0.1;

/**
 * Bed: free-placed furniture (POINT anchor + FREE constraint — aligns to a
 * wall when placed against one, rotates with `R` otherwise). Classic
 * floor-plan symbol: mattress outline, pillow(s) at the head, a blanket
 * line across, and a turned-back fold at the foot corner. The head is at
 * local +Y so the auto-generated panel icon shows the pillows at the top
 * (wall auto-alignment rotates to the nearest quarter-turn regardless).
 * The `type` param switches one centered pillow (single) or two
 * side-by-side (double).
 */
function buildBedGlyph(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;

  pushRect(out, frame, -hw, hw, -hd, hd);

  // Pillows at the head (+depth/2), scaled to the footprint.
  const pillowDepth = Math.min(0.45, frame.depth * 0.22);
  const pillowN1 = hd - PILLOW_MARGIN;
  const pillowN0 = pillowN1 - pillowDepth;
  if (params.kind === 'double') {
    const pillowWidth = (frame.width - 3 * PILLOW_MARGIN) / 2;
    pushRect(out, frame, -hw + PILLOW_MARGIN, -hw + PILLOW_MARGIN + pillowWidth, pillowN0, pillowN1);
    pushRect(out, frame, hw - PILLOW_MARGIN - pillowWidth, hw - PILLOW_MARGIN, pillowN0, pillowN1);
  } else {
    const pillowWidth = Math.min(0.8, frame.width * 0.7);
    pushRect(out, frame, -pillowWidth / 2, pillowWidth / 2, pillowN0, pillowN1);
  }

  // Blanket line across the mattress, below the pillows.
  const blanketN = pillowN0 - BLANKET_GAP;
  pushSegment(out, at(frame, -hw, blanketN), at(frame, hw, blanketN), frame.z);

  // Turned-back fold at the foot corner.
  const fold = Math.min(0.35, frame.width * 0.25);
  pushSegment(out, at(frame, hw - fold, -hd), at(frame, hw, -hd + fold), frame.z);

  return out;
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'bed',
    label: t('Bed'),
    category: SketchObjectCategory.FURNITURE,
    anchor: SketchObjectAnchorKind.POINT,
    constraint: { kind: 'FREE' },
    defaultSize: { width: 1.6, depth: 2.1 },
    params: PARAMS,
    glyph: { kind: 'parametric', build: buildBedGlyph },
  }),
);
